import { MODEL_NAMES } from '../constants/index';
import { conversationHasExactContacts, isSameConversation } from '../utils/messaging/conversation';
import { didCancel, task } from 'ember-concurrency';
import { filterSystemAndCurrentUser } from '../utils/messaging/user';
import Service, { service } from '@ember/service';

/**
 * Manages the client-side lifecycle and state transitions of conversations:
 * applying updates from the server, removing inaccessible or discarded conversations,
 * replacing draft conversations with persisted records, and refreshing open threads.
 */
export default class ConversationLifecycleService extends Service {
  @service intl;
  @service messaging;
  @service messagingLogging;
  @service messagingNavigation;
  @service store;
  @service('spds/toast') toast;

  // Whether any caller that joined the in-flight evacuation wants the toast; the drop task
  // keeps only the first caller's arguments, so intent is merged here instead.
  #notifyOnLeave = false;

  // Single-flight evacuation of the open thread, shared by `remove` and `removeAll` so an
  // overlap navigates and toasts once.
  leaveOpenConversationTask = task({ drop: true }, async () => {
    try {
      await this.messagingNavigation.toConversationList({ recordHistory: false });
      if (this.#notifyOnLeave) {
        this.toast.error(this.intl.t('emc.conversation_no_longer_available'));
      }
    } finally {
      this.#notifyOnLeave = false;
    }
  });

  removeAllTask = task({ drop: true }, async () => {
    const wasViewing = Boolean(this.messagingNavigation.currentConversationHandler?.conversation);
    const conversations = [...(this.messaging.conversations?.content ?? [])];

    try {
      conversations.forEach(conversation => this.#dropConversationFromUi(conversation));

      if (wasViewing) {
        await this.#leaveOpenConversation(true);
      }
    } catch (err) {
      if (didCancel(err)) return;
      this.#logError(err, 'removeAll');
    } finally {
      conversations.forEach(conversation => this.#unloadConversationRecord(conversation));
    }
  });

  replaceTask = task({ enqueue: true }, async (replacedConversationId, newConversation) => {
    try {
      const replacedId = String(replacedConversationId);
      const capturedDraft = this.#captureMessageDraft(replacedId);
      const newRecord = this.#ensureConversationInStore(newConversation.data.id, newConversation);
      if (!newRecord) {
        throw new Error(`Conversation ${newConversation.data.id} missing after replace push`);
      }

      this.#transferMessageDraft(capturedDraft, newRecord);

      await this.messagingNavigation.replaceConversationInNavigationState(replacedId, newRecord);
      this.messaging.replaceConversationInList(replacedId, newRecord);
      this.#unloadConversation(replacedId);
    } catch (err) {
      if (didCancel(err)) return;
      this.#logError(err, 'replace', { replacedConversationId });
    }
  });

  /**
   * Applies an updated conversation payload into the store and active conversation list.
   * If the payload is marked disabled for the viewer, it triggers removal instead.
   *
   * @param {Object} payload - JSON:API conversation payload from websocket broadcast
   * @param {Object} [options]
   * @param {boolean} [options.notify=true] - Whether to toast if access is revoked
   */
  async apply(payload, options = {}) {
    if (this.#shouldIgnorePayload(payload)) {
      return;
    }

    const record = this.#ensureConversationInStore(payload.data.id, payload);

    if (record.disabled) {
      if (this.messaging.groupMessagingEnabled) {
        await this.remove(record.id, { notify: options.notify ?? true });
      }
      return;
    }

    this.#syncConversationInList(record);
    this.refreshOpen({ conversation: record });
  }

  /**
   * Removes conversations the viewer can no longer access, or which were discarded/left.
   * Idempotent. Ignored while `removeAll` runs, and while the open thread is already being evacuated.
   *
   * @param {string|number} conversationId
   * @param {Object} [options]
   * @param {boolean} [options.notify=false] - Whether to show the "no longer available" error toast
   */
  async remove(conversationId, { notify = false } = {}) {
    const id = String(conversationId);
    const wasViewing = this.messagingNavigation.isViewingConversation(id);
    if (wasViewing && this.leaveOpenConversationTask.isRunning) {
      this.#notifyOnLeave ||= notify;
      return;
    }
    if (this.removeAllTask.isRunning) {
      return;
    }

    try {
      this.messagingNavigation.invalidateHistory(id);
      this.messaging.removeConversationFromList(id);
      this.messaging.currentProfile?.removeUnreadConversation(id, { shouldTrack: false });

      if (wasViewing) {
        await this.#leaveOpenConversation(notify);
      }
    } catch (err) {
      if (didCancel(err)) return;
      this.#logError(err, 'remove', { conversationId: id });
    } finally {
      this.#unloadConversation(id);
    }
  }

  /**
   * Removes all visible conversations when the viewer's own messaging is disabled.
   * Shows a single notification toast if the viewer was currently inside a conversation.
   * Concurrent calls collapse into the one already running.
   */
  async removeAll() {
    try {
      await this.removeAllTask.perform();
    } catch (err) {
      if (didCancel(err)) return;
      throw err;
    }
  }

  async #leaveOpenConversation(notify) {
    this.#notifyOnLeave ||= notify;
    await this.leaveOpenConversationTask.perform();
  }

  #dropConversationFromUi(conversation) {
    if (conversation.id !== null && conversation.id !== undefined) {
      this.messagingNavigation.invalidateHistory(conversation.id);
      this.messaging.currentProfile?.removeUnreadConversation(conversation.id, {
        shouldTrack: false,
      });
    }
    this.messaging.removeConversationRecord(conversation);
  }

  /**
   * Marks a conversation as read by normalizing payload and removing it from currentProfile.
   *
   * @param {Object} payload
   */
  markAsRead(payload) {
    const conversation = this.store.normalize(MODEL_NAMES.CONVERSATION, payload.data).data;
    this.messaging.currentProfile?.removeUnreadConversation(conversation.id);
  }

  /**
   * Atomically replaces an existing conversation record with a new one across draft text,
   * navigation stack/history, active conversations list, and store.
   *
   * @param {string|number} replacedConversationId
   * @param {Object} newConversation - JSON:API payload of the persisted conversation
   */
  async replace(replacedConversationId, newConversation) {
    try {
      await this.replaceTask.perform(replacedConversationId, newConversation);
    } catch (err) {
      if (didCancel(err)) return;
      throw err;
    }
  }

  /**
   * Triggers a reload of the current conversation from the show endpoint if it is inactive,
   * has conflicts, or if force is true.
   *
   * @param {Object} [options]
   * @param {boolean} [options.force=false]
   * @param {Object} [options.conversation=null] - Optional conversation record to check against current route
   */
  refreshOpen({ force = false, conversation = null } = {}) {
    const handler = this.messagingNavigation.currentConversationHandler;
    const openConversation = handler?.conversation;

    if (!openConversation) {
      return;
    }

    if (conversation && !isSameConversation(openConversation, conversation)) {
      return;
    }

    if (force || openConversation.inactive || handler.inactiveReasons?.length) {
      handler.reloadFromShow.perform();
    }
  }

  #shouldIgnorePayload(payload) {
    return !this.messaging.groupMessagingEnabled && this.#payloadDisabled(payload);
  }

  #syncConversationInList(record) {
    if (!this.#conversationInList(this.messaging.conversations, record.id)) {
      this.#addOrReplaceConversationRecord(this.messaging.conversations, record);
    }
  }

  #captureMessageDraft(replacedId) {
    const record = this.store.peekRecord(MODEL_NAMES.CONVERSATION, replacedId);
    return record?.messageDraft;
  }

  #transferMessageDraft(capturedDraft, newRecord) {
    if (capturedDraft) {
      newRecord.messageDraft ||= capturedDraft;
    }
  }

  #addOrReplaceConversationRecord(conversations, persistedConversation) {
    const persistedConversationUsers = filterSystemAndCurrentUser(
      persistedConversation.users,
      this.messaging.currentProfile?.user
    );
    const matchingUnsavedConversation = conversations?.content?.find(
      conv =>
        conv.isNew &&
        conversationHasExactContacts(conv, persistedConversationUsers, {
          isCouple: persistedConversation.isCouple,
        })
    );

    if (matchingUnsavedConversation) {
      persistedConversation.messageDraft = matchingUnsavedConversation.messageDraft;

      const index = conversations.content.indexOf(matchingUnsavedConversation);
      conversations.replace(index, 1, [persistedConversation]);
    } else {
      conversations?.unshiftObjects?.([persistedConversation]);
    }
  }

  #conversationInList(conversations, conversationId) {
    return (
      conversations?.content?.some(conv =>
        conv.isSameId ? conv.isSameId(conversationId) : String(conv.id) === String(conversationId)
      ) ?? false
    );
  }

  #ensureConversationInStore(conversationId, payload) {
    this.store.pushPayload(MODEL_NAMES.CONVERSATION, payload);
    return this.store.peekRecord(MODEL_NAMES.CONVERSATION, conversationId);
  }

  #unloadConversationRecord(conversation) {
    if (!conversation.isDestroyed && !conversation.isDestroying) {
      this.store.unloadRecord(conversation);
    }
  }

  #unloadConversation(id) {
    const record = this.store.peekRecord(MODEL_NAMES.CONVERSATION, id);
    if (!record) {
      return;
    }

    this.#unloadConversationRecord(record);
  }

  #logError(err, action, extra = {}) {
    this.messagingLogging.error(err, { context: 'conversation-lifecycle', action, ...extra });
  }

  #payloadDisabled(payload) {
    return payload.data?.attributes?.disabled === true;
  }
}
